/**
 * Add `SchedulingFlowRouteName` to the `RouteName` type, and `routeNames` object in
 * the app.
 *
 * Example:
 * ```
 * export type RouteName = ProRouteName | SchedulingFlowRouteName;
 *
 * export const routeNames = {
 *   ...ProRouteName,
 *   ...SchedulingFlowRouteName,
 * };
 * ```
 */
var SchedulingFlowRouteName;
(function (SchedulingFlowRouteName) {
    /**
     * The parent route. Used for loading tracking.
     */
    SchedulingFlowRouteName["SCHEDULING_FLOW"] = "SchedulingFlow";
    /**
     * Step 1
     *
     * The user can see details about the visit.
     * The user can set an address for the visit.
     */
    SchedulingFlowRouteName["SCHEDULING_FLOW_ADDRESS"] = "SchedulingFlow/Address";
    /**
     * Step 2 (if this is the flexible scheduling flow)
     *
     * The user can select the flexible option for the visit.
     */
    SchedulingFlowRouteName["SCHEDULING_FLOW_FLEXIBLE_OPTION"] = "SchedulingFlow/FlexibleOption";
    /**
     * Step 3
     *
     * The user can select the date range for the visit.
     */
    SchedulingFlowRouteName["SCHEDULING_FLOW_DATE_RANGE"] = "SchedulingFlow/DateRange";
    /**
     * Step 4
     *
     * The user can select the time range for the visit.
     */
    SchedulingFlowRouteName["SCHEDULING_FLOW_TIME_RANGE"] = "SchedulingFlow/TimeRange";
    /**
     * Step 2 (if this is not the flexible scheduling flow)
     *
     * If the user chooses a nurse visit, the user can select a time slot for the visit
     *
     * If the user chooses a lab visit, the user can select the lab they wish to visit
     */
    SchedulingFlowRouteName["SCHEDULING_FLOW_TIME"] = "SchedulingFlow/Time";
    /**
     * Step 5.A (if this is not the flexible scheduling flow and the user has selected a lab visit.
     * After fulfillment integration with ExamOne, shown IFF Place.integration.name !== 'examone')
     *
     * The user can select a date for the visit
     */
    SchedulingFlowRouteName["SCHEDULING_FLOW_WALK_IN_DATE"] = "SchedulingFlow/WalkInDate";
    /**
     * Step 5.B (if this is not the flexible scheduling flow and the user has selected a lab visit.
     * After fulfillment integration with ExamOne, shown IFF Place.integration.name === 'examone')
     *
     * The user can select a specific date and time for the visit through ExamOne's ScheduleNow API
     */
    SchedulingFlowRouteName["SCHEDULING_FLOW_FACILITY_TIME"] = "SchedulingFlow/FacilityTime";
    /**
     * Finalization Step (second to last step)
     *
     * This used to be the final step of the scheduling flow, but now
     * we are displaying a confirmation step after finalization using
     * similar data to this step.
     *
     * The user can:
     * - Review details about the visit.
     * - Add apartment number or additional instructions to address.
     * - The user can pay for the visit.
     */
    SchedulingFlowRouteName["SCHEDULING_FLOW_VISIT_OVERVIEW"] = "SchedulingFlow/VisitOverview";
    /**
     * Final Step
     *
     * The visit is now finalized. We display a review step with
     * similar data to the data on `SchedulingFlowCheckout.vue` of the
     * `SchedulingFlow/VisitOverview` step.
     *
     * The user can:
     * - Review details about the visit.
     * - View instructions for the visit.
     * - Navigate to the visit details page.
     */
    SchedulingFlowRouteName["SCHEDULING_FLOW_REVIEW"] = "SchedulingFlow/Review";
    SchedulingFlowRouteName["SCHEDULING_FLOW_RESCHEDULE_LOADING"] = "SchedulingFlow/RescheduleLoading";
    /**
     * Additional (patient initiated) steps
     *
     * - Complete profile, the user inputs the missings fields of their
     *   profile after checking out.
     * - Practice information, the user inputs some information about their
     *   practice.
     */
    SchedulingFlowRouteName["SCHEDULING_FLOW_COMPLETE_PROFILE"] = "SchedulingFlow/CompleteProfile";
    SchedulingFlowRouteName["SCHEDULING_FLOW_PRACTICE_INFO"] = "SchedulingFlow/PracticeInfo";
})(SchedulingFlowRouteName || (SchedulingFlowRouteName = {}));

export { SchedulingFlowRouteName };
